import {
  ChevronRightIcon,
  CopyIcon,
  ExternalLinkIcon,
  UserIcon,
} from "lucide-react"
import { Fragment, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { TableCell, TableRow } from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { FRONTEND_ROUTES } from "@/constants"
import type { PaymentMethodNameEnum } from "@/enums/orders"
import { paymentMethodLabels } from "@/enums/orders"
import {
  formatDocument,
  formatMoney,
  formatPhoneNumber,
} from "@/helpers/format"
import type { PeriodicityEnum } from "@/helpers/get-periodicity"
import { getPeriodicity } from "@/helpers/get-periodicity"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { usePaymentMethodCatalog } from "@/services/admin/payment-method-catalog"
import type { Subscription } from "@/types/subscriptions"
import { DateHelper } from "@/utils"

import { buttonVariants } from "../ui/button"
import { SubscriptionsStatusBadge } from "./status-badge"
import { SubscriptionsTimeline } from "./subscription-timeline"
import { getPaymentMethodColumnLabel } from "./SubscriptionsHelper"

interface SubscriptionsTableRowProps {
  subscription: Subscription
  filteredPaymentMethod?: string
}

export const SubscriptionsTableRow = ({
  subscription,
  filteredPaymentMethod,
}: SubscriptionsTableRowProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const { data: paymentMethodCatalog } =
    usePaymentMethodCatalog()

  const customerName =
    subscription.customer?.fullName || "-"
  const customerDocument = subscription.customer?.document
    ? formatDocument(subscription.customer.document)
    : "-"
  const customerEmail = subscription.customer?.email || "-"
  const customerPhone = useMemo(() => {
    const rawPhone = subscription.customer?.mobilePhone
    if (!rawPhone) return "-"
    const digits = rawPhone.replaceAll(/\D/g, "")
    if (digits.length === 10 || digits.length === 11) {
      return formatPhoneNumber(digits) || rawPhone
    }
    return rawPhone
  }, [subscription.customer?.mobilePhone])

  const cyclesCount = subscription.billingCycles || 0

  const formattedDate = useMemo(() => {
    if (!subscription.startDate) return "-"
    return DateHelper.formatDate(
      subscription.startDate,
      "dd/MM/yyyy"
    )
  }, [subscription.startDate])

  const periodicity = getPeriodicity(
    subscription.recurringUnit as PeriodicityEnum
  )

  const handleCopy = async (
    text: string,
    message: string
  ) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: message, variant: "success" })
    } catch {
      toast({
        title: t("common.error"),
        description: t("common.copyError"),
        variant: "destructive",
      })
    }
  }

  return (
    <Fragment>
      <TableRow className="even:bg-zinc-50">
        {/* Código + Status */}
        <TableCell className="align-top">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div
                className="hidden cursor-pointer items-center gap-1.5 md:flex"
                onClick={() =>
                  setIsExpanded((prev) => !prev)
                }
              >
                <ChevronRightIcon
                  size={14}
                  className={cn(
                    "shrink-0 text-muted-foreground transition-transform duration-200",
                    isExpanded && "rotate-90"
                  )}
                />
                <span className="font-semibold">
                  {subscription.code}
                </span>
              </div>
              <div className="flex items-center gap-1 md:hidden">
                <span className="font-semibold">
                  {subscription.code}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() =>
                        handleCopy(
                          subscription.code,
                          t(
                            "subscriptions.table.codeCopied"
                          )
                        )
                      }
                      className="rounded p-1 hover:bg-muted"
                    >
                      <CopyIcon
                        size={12}
                        className="text-muted-foreground"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("common.copy")}
                  </TooltipContent>
                </Tooltip>
              </div>
              {/* Status badge mobile */}
              <div className="md:hidden">
                <SubscriptionsStatusBadge
                  status={subscription.status}
                />
              </div>
              {/* Copy button desktop */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() =>
                      handleCopy(
                        subscription.code,
                        t("subscriptions.table.codeCopied")
                      )
                    }
                    className="hidden rounded p-1 hover:bg-muted md:block"
                  >
                    <CopyIcon
                      size={12}
                      className="text-muted-foreground"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("common.copy")}
                </TooltipContent>
              </Tooltip>
            </div>
            {/* Status badge desktop */}
            <div className="hidden md:block">
              <SubscriptionsStatusBadge
                status={subscription.status}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {formattedDate}
            </p>

            {/* Mobile: show all info */}
            <div className="mt-3 space-y-3 border-t pt-3 md:hidden">
              {/* ID */}
              <div className="rounded-md bg-muted/50 p-2">
                <p className="text-[10px] font-medium uppercase text-muted-foreground">
                  ID
                </p>
                <p className="truncate font-mono text-xs">
                  {subscription.id}
                </p>
              </div>

              {/* Cliente */}
              <div className="flex items-start gap-2 rounded-md border p-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <UserIcon
                    size={14}
                    className="text-primary"
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="truncate text-sm font-medium">
                    {customerName}
                  </p>
                  {subscription.customer?.document && (
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground">
                        {customerDocument}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            subscription.customer.document,
                            t(
                              "subscriptions.table.documentCopied"
                            )
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>
                  )}
                  {subscription.customer?.mobilePhone && (
                    <div className="flex items-center gap-1">
                      <p className="truncate text-xs text-muted-foreground">
                        {customerPhone}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            subscription.customer
                              .mobilePhone,
                            t(
                              "subscriptions.table.phoneCopied"
                            )
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>
                  )}
                  <p className="truncate text-xs text-muted-foreground">
                    {customerEmail}
                  </p>
                </div>
              </div>

              {/* Periodicidade e Ciclos */}
              <div className="flex items-center justify-between rounded-md bg-muted/30 p-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    {periodicity}
                  </span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
                    {cyclesCount}{" "}
                    {cyclesCount === 1
                      ? t("subscriptions.table.cycle")
                      : t("subscriptions.table.cycles")}
                  </span>
                </div>
                <span className="text-base font-bold text-primary">
                  {formatMoney(
                    subscription.totalAmount / 100
                  )}
                </span>
              </div>

              {/* Ações */}
              <div className="flex items-center gap-2">
                <Link
                  to={`${FRONTEND_ROUTES.SUBSCRIPTIONS.href}/details/${subscription.id || subscription.code}`}
                  className={cn(
                    buttonVariants({
                      size: "sm",
                      variant: "default",
                    }),
                    "flex-1"
                  )}
                >
                  <ExternalLinkIcon
                    size={14}
                    className="mr-1.5"
                  />
                  {t("common.viewDetails")}
                </Link>
              </div>
            </div>
          </div>
        </TableCell>

        {/* Cliente */}
        <TableCell className="hidden align-top lg:table-cell">
          <div className="flex items-start gap-2">
            <Link
              to={`/customers/details/${subscription.customer?.id}`}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 transition-colors hover:bg-primary/20"
              title={t("subscriptions.table.viewCustomer")}
            >
              <UserIcon
                size={14}
                className="text-primary"
              />
            </Link>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="truncate font-medium">
                {customerName}
              </p>
              {subscription.customer?.document && (
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground">
                    {customerDocument}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            subscription.customer.document,
                            t(
                              "subscriptions.table.documentCopied"
                            )
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("common.copy")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {subscription.customer?.mobilePhone && (
                <div className="flex items-center gap-1">
                  <p className="truncate text-xs text-muted-foreground">
                    {customerPhone}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            subscription.customer
                              .mobilePhone,
                            t(
                              "subscriptions.table.phoneCopied"
                            )
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("common.copy")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {subscription.customer?.email && (
                <div className="flex items-center gap-1">
                  <p
                    className="max-w-[160px] truncate text-xs text-muted-foreground"
                    title={subscription.customer.email}
                  >
                    {subscription.customer.email}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            subscription.customer.email,
                            t(
                              "subscriptions.table.emailCopied"
                            )
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("common.copy")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </TableCell>

        {/* Qtd. Ciclos */}
        <TableCell className="hidden text-center align-top xl:table-cell">
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-2 text-sm font-medium">
            {cyclesCount}
          </span>
        </TableCell>

        {/* Periodicidade */}
        <TableCell className="hidden align-top lg:table-cell">
          <span className="text-sm">{periodicity}</span>
        </TableCell>

        {/* Meio de Pagamento */}
        <TableCell className="hidden align-top lg:table-cell">
          <span className="text-sm">
            {getPaymentMethodColumnLabel(
              subscription,
              (type) =>
                paymentMethodCatalog?.find(
                  (item) => item.paymentMethod === type
                )?.description ||
                paymentMethodLabels[
                  type as PaymentMethodNameEnum
                ] ||
                type,
              filteredPaymentMethod
            )}
          </span>
        </TableCell>

        {/* Valor */}
        <TableCell className="hidden text-end align-top sm:table-cell">
          <p className="font-semibold">
            {formatMoney(subscription.totalAmount / 100)}
          </p>
        </TableCell>

        {/* Data de Início */}
        <TableCell className="hidden align-top lg:table-cell">
          <p className="text-sm text-muted-foreground">
            {formattedDate}
          </p>
        </TableCell>

        {/* Ações */}
        <TableCell className="hidden align-top md:table-cell">
          <div className="flex items-center justify-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${FRONTEND_ROUTES.SUBSCRIPTIONS.href}/details/${subscription.id || subscription.code}`}
                  className={cn(
                    buttonVariants({
                      size: "icon",
                      variant: "ghost",
                    }),
                    "h-8 w-8"
                  )}
                >
                  <ExternalLinkIcon size={16} />
                  <span className="sr-only">
                    {t("common.viewDetails")}
                  </span>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                {t("common.viewDetails")}
              </TooltipContent>
            </Tooltip>
            {/* OMITIDO: Botão de reenvio de email
            {canResendEmail && subscription.checkoutUrl && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() =>
                        setShowResendEmailDialog(true)
                      }
                      className={cn(
                        buttonVariants({
                          size: "icon",
                          variant: "ghost",
                        }),
                        "h-8 w-8"
                      )}
                    >
                      <MailIcon size={16} />
                      <span className="sr-only">
                        {t(
                          "subscriptions.table.resendEmail"
                        )}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("subscriptions.table.resendEmail")}
                  </TooltipContent>
                </Tooltip>
                <ResendEmailSibscriptionDialog
                  data={subscription}
                  open={showResendEmailDialog}
                  setOpen={setShowResendEmailDialog}
                />
              </>
            )}
            */}
          </div>
        </TableCell>
      </TableRow>

      {/* Timeline expandido */}
      {isExpanded && (
        <TableRow className="bg-muted/30">
          <TableCell colSpan={8} className="p-4">
            <SubscriptionsTimeline
              subscription={subscription}
            />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}
